import React from 'react';
import cx from 'clsx';
import { makeStyles, Theme } from '@material-ui/core/styles';
import LettercardSelector from '../components/LetterCardSelect';
import RadioGroup from '@material-ui/core/RadioGroup';
import Grid from '@material-ui/core/Grid';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import { useTranslation } from 'react-i18next';
import Button from '@material-ui/core/Button';
import { StudentLoginType } from 'types/schema-generated-types';
import CancelIcon from '@material-ui/icons/Cancel';

import { SecretCodeSelectProps } from '../types';

const useStyles = makeStyles<Theme>((theme: Theme) => {
  return {
    popper: {
      zIndex: theme.zIndex.modal,
      backgroundColor: theme.palette.common.white,
      padding: theme.spacing(3),
      boxShadow: theme.shadows[6]
    },
    clearAllButton: {
      float: 'right',
      marginRight: theme.spacing(2)
    },
    radio: {},
    secretCodeLabel: {
      display: 'inline-block',
      marginTop: theme.spacing(0.75)
    },
    textRadioContainer: {
      paddingTop: theme.spacing(2)
    },
    radioGroup: {
      display: 'flex',
      flexDirection: 'row'
    },
    textBoxContainer: {
      width: '50%',
      paddingBottom: theme.spacing(2)
    },
    endButtonContainer: {
      float: 'right'
    },
    helperText: {
      marginTop: theme.spacing(2)
    },
    error: {
      margin: `${theme.spacing(0.5)} 0px`
    },
    cancelButton: {
      marginRight: theme.spacing(3)
    },
    label: {
      ...theme.typography.body1,
      fontWeight: 500,
      marginLeft: 0
    },
    radioText: {
      ...theme.typography.subtitle1,
      marginLeft: 0
    },
    letterCardHeaders: {
      marginTop: theme.spacing(2)
    },
    secretCodeHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '120%'
    }
  };
});

const SecretCodeSelect: React.FC<SecretCodeSelectProps> = ({
  disabled,
  maxTextBoxLength = 16,
  showUnselected = true,
  loginType = StudentLoginType.LETTER_CARDS,
  onSecretCodeChange,
  error,
  secretCode = '',
  ...rest
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [internalLoginType, setLoginType] = React.useState(loginType);
  const [secretCodeValues, setSecretCodeValues] = React.useState(
    loginType === StudentLoginType.LETTER_CARDS
      ? { textBox: '', letterBox: secretCode }
      : { textBox: secretCode, letterBox: '' }
  );

  const handleTextBoxChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    if (e.target.value.length <= maxTextBoxLength) {
      if (/^[A-Z0-9]+$/i.test(e.target.value) || e.target.value === '') {
        setSecretCodeValues({ ...secretCodeValues, textBox: e.target.value.toUpperCase() });
        if (onSecretCodeChange) {
          onSecretCodeChange(internalLoginType, e.target.value.toUpperCase());
        }
      }
    }
  };
  const handleLetterBoxChange: (value: string) => void = (value: string) => {
    setSecretCodeValues({ ...secretCodeValues, letterBox: value });
    if (onSecretCodeChange) {
      onSecretCodeChange(internalLoginType, value);
    }
  };
  const handleSelectionClear: React.MouseEventHandler<HTMLSpanElement> = () => {
    setSecretCodeValues({ textBox: '', letterBox: '' });
    if (onSecretCodeChange) {
      onSecretCodeChange(internalLoginType, '');
    }
  };

  const handleLoginTypeChange: React.ChangeEventHandler<HTMLInputElement> = event => {
    const value = event.currentTarget.value as StudentLoginType;
    setLoginType(value);
    if (onSecretCodeChange) {
      onSecretCodeChange(
        value,
        value === StudentLoginType.LETTER_CARDS
          ? secretCodeValues.letterBox
          : secretCodeValues.textBox
      );
    }
  };

  return (
    <div>
      <Typography className={classes.label}>Secret Code</Typography>
      <RadioGroup
        className={classes.radioGroup}
        onChange={handleLoginTypeChange}
        value={internalLoginType}
      >
        <FormControlLabel
          classes={{ root: classes.radio, label: classes.radioText }}
          value={StudentLoginType.LETTER_CARDS}
          control={<Radio color="secondary" />}
          label={t('Letter Cards')}
        />
        <FormControlLabel
          value={StudentLoginType.TEXTBOX}
          classes={{ root: classes.radio, label: classes.radioText }}
          control={<Radio color="secondary" />}
          label={t('Text Box')}
        />
      </RadioGroup>
      {(internalLoginType === StudentLoginType.LETTER_CARDS || showUnselected) && (
        <div>
          <LettercardSelector
            displayDirection="column"
            secretCode={secretCodeValues.letterBox}
            onChange={handleLetterBoxChange}
            disabled={internalLoginType !== StudentLoginType.LETTER_CARDS}
            LettercardsTitle={
              <Typography variant="caption" className={classes.letterCardHeaders}>
                Select between two to four letter cards
              </Typography>
            }
            SecretCodeTitle={
              <Grid className={cx(classes.letterCardHeaders, classes.secretCodeHeader)}>
                <Typography variant="caption">{t('Secret Code')}</Typography>
                <Button
                  variant="text"
                  size="small"
                  disableElevation
                  startIcon={<CancelIcon />}
                  className={classes.clearAllButton}
                  onClick={!disabled ? handleSelectionClear : undefined}
                >
                  {t('Clear All')}
                </Button>
              </Grid>
            }
          />
          {error && (
            <Typography variant="subtitle2" color="primary" className={classes.error}>
              {error}
            </Typography>
          )}
        </div>
      )}
      {(internalLoginType === StudentLoginType.TEXTBOX || showUnselected) && (
        <div className={classes.textBoxContainer}>
          <TextField
            disabled={internalLoginType !== StudentLoginType.TEXTBOX}
            variant="outlined"
            size="small"
            fullWidth
            helperText={error}
            error={Boolean(error)}
            placeholder={t('Secret Code')}
            value={secretCodeValues.textBox}
            name="secretCode"
            onChange={handleTextBoxChange}
          />
        </div>
      )}
    </div>
  );
};

export default SecretCodeSelect;
